import {
  AssistanceTypeEnum,
  ConversationAuthorEnum,
  DiagnosticCodeStateEnum,
  GuidedCheckStatusEnum,
  IssueReportChannelEnum,
  ServiceProviderStatusEnum,
  ServiceRequestPriorityEnum,
  ServiceRequestStatusEnum,
} from '../../../enums';
import { ServiceProviderDto, ServiceRequestDto } from '../../../models';

const cincinnatiProvidersMock: ServiceProviderDto[] = [
  {
    id: 'p-1',
    name: 'Demo Diesel Service',
    address: '100 Example Rd, Cincinnati, OH',
    phone: '(513) 555-0101',
    specializations: ['Diesel engines', 'Fuel systems', 'Heavy duty'],
    assistanceTypes: [AssistanceTypeEnum.SHOP_VISIT, AssistanceTypeEnum.MOBILE_MECHANIC],
    distanceMiles: 4.2,
    mobileService: true,
    status: ServiceProviderStatusEnum.SUGGESTED,
  },
  {
    id: 'p-2',
    name: 'Demo Truck Repair',
    address: '250 Sample Ave, Cincinnati, OH',
    phone: '(513) 555-0102',
    specializations: ['Class 8 trucks', 'Electrical', 'Turbochargers'],
    assistanceTypes: [AssistanceTypeEnum.SHOP_VISIT, AssistanceTypeEnum.TOWING],
    distanceMiles: 6.8,
    mobileService: false,
    status: ServiceProviderStatusEnum.SUGGESTED,
  },
  {
    id: 'p-3',
    name: 'Demo Mobile Mechanic',
    address: '500 Demo St, Cincinnati, OH',
    phone: '(513) 555-0103',
    specializations: ['Roadside diagnostics', 'Minor repairs'],
    assistanceTypes: [AssistanceTypeEnum.MOBILE_MECHANIC],
    distanceMiles: 9.1,
    mobileService: true,
    status: ServiceProviderStatusEnum.SUGGESTED,
  },
];

export const serviceRequestsMock: ServiceRequestDto[] = [
  {
    id: '1',
    number: 'SR-001',
    status: ServiceRequestStatusEnum.AWAITING_APPROVAL,
    priority: ServiceRequestPriorityEnum.HIGH,
    createdAt: '2026-10-21T10:15:00',
    updatedAt: '2026-10-21T10:24:00',
    vehicle: {
      id: 'v-18',
      vehicleId: 'Truck 018',
      vin: '1XKYD49X0LJ000018',
      model: 'Kenworth T680',
      year: 2020,
      mileage: 412580,
      engine: 'PACCAR MX-13',
      fuelType: 'Diesel',
    },
    driver: {
      id: 'd-1',
      fullName: 'Nolan Geidt',
      phone: '(513) 555-0180',
    },
    location: {
      address: 'I-75 N, Exit 10',
      city: 'Cincinnati',
      state: 'OH',
      lat: 39.1031,
      lng: -84.512,
      updatedAt: '2026-10-21T10:23:00',
    },
    issue: {
      title: 'Loss of engine power with warning light',
      driverAccount: 'The truck is losing power. A warning light is on.',
      channel: IssueReportChannelEnum.VOICE,
      symptoms: ['Loss of power under load', 'Check engine light on', 'No smoke or unusual noise'],
      photos: ['Dashboard warning light', 'Engine bay'],
    },
    diagnostics: {
      capturedAt: '2026-10-21T10:16:00',
      codes: [
        {
          code: 'SPN 102 FMI 18',
          description: 'Boost pressure below normal operating range',
          source: 'Engine ECU',
          state: DiagnosticCodeStateEnum.ACTIVE,
          occurredAt: '2026-10-21T10:12:00',
        },
        {
          code: 'SPN 641 FMI 7',
          description: 'VGT actuator not responding properly',
          source: 'Engine ECU',
          state: DiagnosticCodeStateEnum.INACTIVE,
          occurredAt: '2026-10-21T09:58:00',
        },
      ],
      readings: [
        { name: 'Coolant temperature', value: '192 °F' },
        { name: 'Oil pressure', value: '42 psi' },
        { name: 'Boost pressure', value: '8 psi' },
        { name: 'Battery voltage', value: '13.9 V' },
      ],
    },
    assessment: {
      summary: 'Loss of engine power with warning light.',
      cause: 'Not confirmed. Technician diagnosis needed.',
      potentialCauses: ['Turbocharger / VGT actuator fault', 'Boost leak in charge air system'],
      recommendations: [
        'Keep the truck parked while arranging help.',
        'Share symptoms and available fault codes with a technician.',
        'Contact a nearby truck repair shop or mobile service.',
      ],
    },
    checks: [
      {
        id: 'c-1',
        suggestedAction: 'Park in a safe location and turn on hazard lights',
        driverStep: 'Parked on the shoulder after Exit 10',
        outcome: 'Truck parked safely',
        status: GuidedCheckStatusEnum.OK,
        completedAt: '2026-10-21T10:17:00',
      },
      {
        id: 'c-2',
        suggestedAction: 'Check for smoke, unusual noise or overheating',
        driverStep: 'Visual check of engine bay and gauges',
        outcome: 'No smoke or unusual noise. Temperature normal',
        status: GuidedCheckStatusEnum.OK,
        completedAt: '2026-10-21T10:17:00',
      },
      {
        id: 'c-3',
        suggestedAction: 'Restart the engine after 5 minutes and test throttle response',
        driverStep: 'Restarted engine, tested at idle and light throttle',
        outcome: 'Power loss persists, warning light still on',
        status: GuidedCheckStatusEnum.ISSUE_PERSISTS,
        completedAt: '2026-10-21T10:18:00',
      },
    ],
    assistanceType: AssistanceTypeEnum.MOBILE_MECHANIC,
    providers: [
      ...cincinnatiProvidersMock,
      {
        id: 'p-4',
        name: 'Demo Fleet Road Service',
        address: '18 Placeholder Blvd, Covington, KY',
        phone: '(859) 555-0104',
        specializations: ['Diesel engines', 'Turbochargers', 'Roadside diagnostics'],
        assistanceTypes: [AssistanceTypeEnum.MOBILE_MECHANIC, AssistanceTypeEnum.TOWING],
        distanceMiles: 11.5,
        mobileService: true,
        confirmedAvailability: 'On-site in ~45 min',
        estimatedCost: 380,
        status: ServiceProviderStatusEnum.RESPONDED,
        respondedAt: '2026-10-21T10:23:00',
        note: 'Can bring a replacement VGT actuator if needed.',
      },
      {
        id: 'p-5',
        name: 'Demo Heavy Duty Center',
        address: '77 Sample Pkwy, Cincinnati, OH',
        phone: '(513) 555-0105',
        specializations: ['Class 8 trucks', 'Engine overhaul'],
        assistanceTypes: [AssistanceTypeEnum.SHOP_VISIT],
        distanceMiles: 7.4,
        mobileService: false,
        confirmedAvailability: 'Bay available today after 1:00 PM',
        estimatedCost: 520,
        status: ServiceProviderStatusEnum.RESPONDED,
        respondedAt: '2026-10-21T10:24:00',
      },
    ],
    conversation: [
      {
        id: 'm-1',
        author: ConversationAuthorEnum.DRIVER,
        text: 'The truck is losing power. A warning light is on.',
        createdAt: '2026-10-21T10:15:00',
      },
      {
        id: 'm-2',
        author: ConversationAuthorEnum.AI_ASSISTANT,
        text: 'Are you parked safely? Any smoke, unusual noise, or overheating?',
        createdAt: '2026-10-21T10:16:00',
      },
      {
        id: 'm-3',
        author: ConversationAuthorEnum.DRIVER,
        text: 'Parked safely. No smoke or unusual noise. Temperature looks normal.',
        createdAt: '2026-10-21T10:17:00',
      },
      {
        id: 'm-4',
        author: ConversationAuthorEnum.AI_ASSISTANT,
        text: 'Thanks. I will include these details in the issue summary.',
        createdAt: '2026-10-21T10:18:00',
      },
    ],
    timeline: [
      { id: 't-1', text: 'Issue reported: truck losing power.', createdAt: '2026-10-21T10:15:00' },
      { id: 't-2', text: 'Diagnostic data attached.', createdAt: '2026-10-21T10:16:00' },
      { id: 't-3', text: 'Guided checks completed: issue persists.', createdAt: '2026-10-21T10:18:00' },
      { id: 't-4', text: 'Assistance request ready: review and approve.', createdAt: '2026-10-21T10:19:00' },
      { id: 't-5', text: 'Two service providers have responded.', createdAt: '2026-10-21T10:24:00' },
    ],
  },
  {
    id: '2',
    number: 'SR-002',
    status: ServiceRequestStatusEnum.DIAGNOSING,
    priority: ServiceRequestPriorityEnum.MEDIUM,
    createdAt: '2026-10-22T08:42:00',
    updatedAt: '2026-10-22T08:47:00',
    vehicle: {
      id: 'v-24',
      vehicleId: 'Truck 024',
      vin: '3AKJHHDR5MS000024',
      model: 'Freightliner Cascadia',
      year: 2021,
      mileage: 298140,
      engine: 'Detroit DD15',
      fuelType: 'Diesel',
    },
    driver: {
      id: 'd-2',
      fullName: 'Malcolm Gomez',
      phone: '(317) 555-0122',
    },
    location: {
      address: 'I-70 W, Mile 92',
      city: 'Indianapolis',
      state: 'IN',
      lat: 39.7684,
      lng: -86.1581,
      updatedAt: '2026-10-22T08:46:00',
    },
    issue: {
      title: 'Low air pressure warning',
      driverAccount: 'Air pressure warning buzzer keeps going off, gauge drops below 70 psi.',
      channel: IssueReportChannelEnum.TEXT,
      symptoms: ['Air pressure warning buzzer', 'Gauge drops below 70 psi', 'Audible hiss near rear axle'],
      photos: ['Air pressure gauge'],
    },
    diagnostics: {
      capturedAt: '2026-10-22T08:43:00',
      codes: [
        {
          code: 'SPN 117 FMI 1',
          description: 'Brake primary air pressure below normal',
          source: 'Brake controller',
          state: DiagnosticCodeStateEnum.ACTIVE,
          occurredAt: '2026-10-22T08:39:00',
        },
      ],
      readings: [
        { name: 'Primary air pressure', value: '68 psi' },
        { name: 'Secondary air pressure', value: '96 psi' },
        { name: 'Coolant temperature', value: '188 °F' },
      ],
    },
    assessment: {
      summary: 'Primary air system losing pressure.',
      cause: 'Not confirmed. Possible air leak.',
      potentialCauses: ['Leaking air line or fitting near rear axle', 'Faulty air compressor governor'],
      recommendations: [
        'Do not continue driving until pressure is stable.',
        'Listen for leaks with the engine off and brakes released.',
      ],
    },
    checks: [
      {
        id: 'c-1',
        suggestedAction: 'Park in a safe location and set parking brake',
        driverStep: 'Parked at rest area',
        outcome: 'Truck parked safely',
        status: GuidedCheckStatusEnum.OK,
        completedAt: '2026-10-22T08:44:00',
      },
      {
        id: 'c-2',
        suggestedAction: 'Perform air leak-down test',
        status: GuidedCheckStatusEnum.PENDING,
      },
    ],
    providers: [
      {
        id: 'p-6',
        name: 'Demo Brake & Air Systems',
        address: '12 Example Way, Indianapolis, IN',
        specializations: ['Air brakes', 'Air systems'],
        assistanceTypes: [AssistanceTypeEnum.MOBILE_MECHANIC, AssistanceTypeEnum.SHOP_VISIT],
        distanceMiles: 3.6,
        mobileService: true,
        status: ServiceProviderStatusEnum.SUGGESTED,
      },
    ],
    conversation: [
      {
        id: 'm-1',
        author: ConversationAuthorEnum.DRIVER,
        text: 'Air pressure warning buzzer keeps going off, gauge drops below 70 psi.',
        createdAt: '2026-10-22T08:42:00',
      },
      {
        id: 'm-2',
        author: ConversationAuthorEnum.AI_ASSISTANT,
        text: 'Please park safely and set the parking brake. Can you hear any hissing sound?',
        createdAt: '2026-10-22T08:43:00',
      },
      {
        id: 'm-3',
        author: ConversationAuthorEnum.DRIVER,
        text: 'Yes, there is a hiss near the rear axle.',
        createdAt: '2026-10-22T08:45:00',
      },
    ],
    timeline: [
      { id: 't-1', text: 'Issue reported: low air pressure warning.', createdAt: '2026-10-22T08:42:00' },
      { id: 't-2', text: 'Diagnostic data attached.', createdAt: '2026-10-22T08:43:00' },
      { id: 't-3', text: 'Guided checks in progress.', createdAt: '2026-10-22T08:47:00' },
    ],
  },
  {
    id: '3',
    number: 'SR-003',
    status: ServiceRequestStatusEnum.PROVIDER_ASSIGNED,
    priority: ServiceRequestPriorityEnum.CRITICAL,
    createdAt: '2026-10-20T22:05:00',
    updatedAt: '2026-10-20T22:40:00',
    vehicle: {
      id: 'v-7',
      vehicleId: 'Truck 007',
      vin: '4V4NC9EH5HN000007',
      model: 'Volvo VNL 760',
      year: 2017,
      mileage: 688230,
      engine: 'Volvo D13',
      fuelType: 'Diesel',
    },
    driver: {
      id: 'd-3',
      fullName: 'Ava Thompson',
      phone: '(614) 555-0133',
    },
    location: {
      address: 'I-71 S, Mile 121',
      city: 'Columbus',
      state: 'OH',
      lat: 39.9612,
      lng: -82.9988,
      updatedAt: '2026-10-20T22:38:00',
    },
    issue: {
      title: 'Engine overheating, coolant leak',
      driverAccount: 'Temperature gauge spiked and there is steam coming from the front grill.',
      channel: IssueReportChannelEnum.PHOTO,
      symptoms: ['Temperature above normal', 'Steam from front grill', 'Coolant on the ground'],
      photos: ['Steam from grill', 'Coolant puddle', 'Temperature gauge'],
    },
    diagnostics: {
      capturedAt: '2026-10-20T22:06:00',
      codes: [
        {
          code: 'SPN 110 FMI 0',
          description: 'Engine coolant temperature above normal operating range',
          source: 'Engine ECU',
          state: DiagnosticCodeStateEnum.ACTIVE,
          occurredAt: '2026-10-20T22:01:00',
        },
        {
          code: 'SPN 111 FMI 1',
          description: 'Coolant level below normal',
          source: 'Engine ECU',
          state: DiagnosticCodeStateEnum.ACTIVE,
          occurredAt: '2026-10-20T21:57:00',
        },
      ],
      readings: [
        { name: 'Coolant temperature', value: '238 °F' },
        { name: 'Oil pressure', value: '35 psi' },
      ],
    },
    assessment: {
      summary: 'Engine overheating with coolant loss.',
      cause: 'Not confirmed. Likely cooling system leak.',
      potentialCauses: ['Ruptured radiator hose', 'Radiator damage', 'Water pump failure'],
      recommendations: [
        'Shut off the engine immediately.',
        'Do not open the radiator cap while hot.',
        'Arrange towing.',
      ],
    },
    checks: [
      {
        id: 'c-1',
        suggestedAction: 'Shut off the engine and move away from steam',
        driverStep: 'Engine off, driver outside the vehicle',
        outcome: 'Done',
        status: GuidedCheckStatusEnum.OK,
        completedAt: '2026-10-20T22:07:00',
      },
      {
        id: 'c-2',
        suggestedAction: 'Inspect visible hoses for damage',
        driverStep: 'Looked under the hood with flashlight',
        outcome: 'Lower radiator hose split',
        status: GuidedCheckStatusEnum.ISSUE_PERSISTS,
        completedAt: '2026-10-20T22:12:00',
      },
    ],
    assistanceType: AssistanceTypeEnum.TOWING,
    selectedProviderId: 'p-7',
    approvedBy: 'Fleet Manager',
    approvedAt: '2026-10-20T22:20:00',
    providers: [
      {
        id: 'p-7',
        name: 'Demo Towing & Recovery',
        address: '400 Sample Rd, Columbus, OH',
        phone: '(614) 555-0107',
        specializations: ['Heavy duty towing', 'Recovery'],
        assistanceTypes: [AssistanceTypeEnum.TOWING],
        distanceMiles: 5.3,
        mobileService: true,
        confirmedAvailability: 'Truck dispatched, ETA 35 min',
        estimatedCost: 650,
        status: ServiceProviderStatusEnum.SELECTED,
        respondedAt: '2026-10-20T22:28:00',
      },
      {
        id: 'p-8',
        name: 'Demo Cooling Systems',
        address: '22 Example Ct, Columbus, OH',
        specializations: ['Radiators', 'Cooling systems'],
        assistanceTypes: [AssistanceTypeEnum.SHOP_VISIT],
        distanceMiles: 8.9,
        mobileService: false,
        status: ServiceProviderStatusEnum.DECLINED,
        respondedAt: '2026-10-20T22:31:00',
        note: 'No bays available tonight.',
      },
    ],
    conversation: [
      {
        id: 'm-1',
        author: ConversationAuthorEnum.DRIVER,
        text: 'Temperature gauge spiked and there is steam coming from the front grill.',
        createdAt: '2026-10-20T22:05:00',
      },
      {
        id: 'm-2',
        author: ConversationAuthorEnum.AI_ASSISTANT,
        text: 'Please shut off the engine now and step away from the steam. Are you safe?',
        createdAt: '2026-10-20T22:05:00',
      },
      {
        id: 'm-3',
        author: ConversationAuthorEnum.OPERATOR,
        text: 'Towing is on the way, ETA 35 minutes. Stay with the vehicle.',
        createdAt: '2026-10-20T22:40:00',
      },
    ],
    timeline: [
      { id: 't-1', text: 'Issue reported: engine overheating.', createdAt: '2026-10-20T22:05:00' },
      { id: 't-2', text: 'Diagnostic data attached.', createdAt: '2026-10-20T22:06:00' },
      { id: 't-3', text: 'Guided checks completed: radiator hose split.', createdAt: '2026-10-20T22:12:00' },
      { id: 't-4', text: 'Request approved and sent by Fleet Manager.', createdAt: '2026-10-20T22:20:00' },
      { id: 't-5', text: 'Demo Towing & Recovery selected.', createdAt: '2026-10-20T22:40:00' },
    ],
  },
  {
    id: '4',
    number: 'SR-004',
    status: ServiceRequestStatusEnum.IN_PROGRESS,
    priority: ServiceRequestPriorityEnum.LOW,
    createdAt: '2026-10-18T14:20:00',
    updatedAt: '2026-10-18T17:05:00',
    vehicle: {
      id: 'v-31',
      vehicleId: 'Truck 031',
      vin: '1FUJGLDR7CL000031',
      model: 'Freightliner Cascadia',
      year: 2019,
      mileage: 351020,
      engine: 'Detroit DD13',
      fuelType: 'Diesel',
    },
    driver: {
      id: 'd-4',
      fullName: 'Liam Carter',
    },
    location: {
      address: '1200 Industrial Dr',
      city: 'Louisville',
      state: 'KY',
      lat: 38.2527,
      lng: -85.7585,
      updatedAt: '2026-10-18T14:21:00',
    },
    issue: {
      title: 'Trailer lights not working',
      driverAccount: 'Trailer marker and brake lights are out.',
      channel: IssueReportChannelEnum.TEXT,
      symptoms: ['No trailer marker lights', 'No trailer brake lights'],
      photos: [],
    },
    diagnostics: {
      capturedAt: '2026-10-18T14:21:00',
      codes: [],
      readings: [{ name: 'Battery voltage', value: '13.8 V' }],
    },
    assessment: {
      summary: 'Trailer lighting failure.',
      cause: 'Confirmed by technician: corroded 7-way connector.',
      potentialCauses: ['Corroded 7-way connector', 'Blown fuse'],
      recommendations: ['Inspect the 7-way connector.', 'Check trailer light fuses.'],
    },
    checks: [
      {
        id: 'c-1',
        suggestedAction: 'Reconnect the 7-way plug',
        driverStep: 'Unplugged and reconnected',
        outcome: 'Lights still off',
        status: GuidedCheckStatusEnum.ISSUE_PERSISTS,
        completedAt: '2026-10-18T14:25:00',
      },
    ],
    assistanceType: AssistanceTypeEnum.MOBILE_MECHANIC,
    selectedProviderId: 'p-9',
    approvedBy: 'Fleet Manager',
    approvedAt: '2026-10-18T14:32:00',
    providers: [
      {
        id: 'p-9',
        name: 'Demo Truck Electric',
        address: '9 Placeholder Ln, Louisville, KY',
        specializations: ['Electrical', 'Trailer lighting'],
        assistanceTypes: [AssistanceTypeEnum.MOBILE_MECHANIC],
        distanceMiles: 2.7,
        mobileService: true,
        confirmedAvailability: 'Arrived 15:10',
        estimatedCost: 140,
        status: ServiceProviderStatusEnum.SELECTED,
        respondedAt: '2026-10-18T14:40:00',
      },
    ],
    conversation: [
      {
        id: 'm-1',
        author: ConversationAuthorEnum.DRIVER,
        text: 'Trailer marker and brake lights are out.',
        createdAt: '2026-10-18T14:20:00',
      },
      {
        id: 'm-2',
        author: ConversationAuthorEnum.AI_ASSISTANT,
        text: 'Please try reconnecting the 7-way plug and let me know if the lights come back.',
        createdAt: '2026-10-18T14:21:00',
      },
    ],
    timeline: [
      { id: 't-1', text: 'Issue reported: trailer lights not working.', createdAt: '2026-10-18T14:20:00' },
      { id: 't-2', text: 'Request approved and sent by Fleet Manager.', createdAt: '2026-10-18T14:32:00' },
      { id: 't-3', text: 'Demo Truck Electric selected.', createdAt: '2026-10-18T14:45:00' },
      { id: 't-4', text: 'Repair completed, request resolved.', createdAt: '2026-10-18T17:05:00' },
    ],
  },
  {
    id: '5',
    number: 'SR-005',
    status: ServiceRequestStatusEnum.NEW,
    priority: ServiceRequestPriorityEnum.MEDIUM,
    createdAt: '2026-10-22T09:58:00',
    updatedAt: '2026-10-22T09:58:00',
    vehicle: {
      id: 'v-12',
      vehicleId: 'Truck 012',
      vin: '1XPBDP9X1ND000012',
      model: 'Peterbilt 579',
      year: 2022,
      mileage: 145600,
      engine: 'PACCAR MX-13',
      fuelType: 'Diesel',
    },
    driver: {
      id: 'd-5',
      fullName: 'Alex Nik',
    },
    location: {
      address: 'US-50 E',
      city: 'Cincinnati',
      state: 'OH',
      lat: 39.1,
      lng: -84.45,
      updatedAt: '2026-10-22T09:58:00',
    },
    issue: {
      title: 'Flat tire on trailer',
      driverAccount: 'Rear trailer tire is flat.',
      channel: IssueReportChannelEnum.PHOTO,
      symptoms: ['Flat tire, rear trailer axle'],
      photos: ['Flat tire'],
    },
    diagnostics: {
      capturedAt: '2026-10-22T09:58:00',
      codes: [],
      readings: [],
    },
    assessment: {
      summary: 'Flat tire on trailer.',
      cause: 'Not confirmed.',
      potentialCauses: ['Puncture', 'Valve stem failure'],
      recommendations: ['Keep the truck parked.', 'Request mobile tire service.'],
    },
    checks: [
      {
        id: 'c-1',
        suggestedAction: 'Park in a safe location and turn on hazard lights',
        driverStep: 'Parked on the shoulder after Exit 10',
        outcome: 'Truck parked safely',
        status: GuidedCheckStatusEnum.OK,
        completedAt: '2026-10-21T10:17:00',
      },
      {
        id: 'c-2',
        suggestedAction: 'Check for smoke, unusual noise or overheating',
        driverStep: 'Visual check of engine bay and gauges',
        outcome: 'No smoke or unusual noise. Temperature normal',
        status: GuidedCheckStatusEnum.OK,
        completedAt: '2026-10-21T10:17:00',
      },
      {
        id: 'c-3',
        suggestedAction: 'Restart the engine after 5 minutes and test throttle response',
        driverStep: 'Restarted engine, tested at idle and light throttle',
        outcome: 'Power loss persists, warning light still on',
        status: GuidedCheckStatusEnum.ISSUE_PERSISTS,
        completedAt: '2026-10-21T10:18:00',
      },
    ],
    providers: cincinnatiProvidersMock,
    conversation: [
      {
        id: 'm-1',
        author: ConversationAuthorEnum.DRIVER,
        text: 'Rear trailer tire is flat.',
        createdAt: '2026-10-22T09:58:00',
      },
    ],
    timeline: [{ id: 't-1', text: 'Issue reported: flat tire on trailer.', createdAt: '2026-10-22T09:58:00' }],
  },
];
